import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, Tooltip } from '@mantine/core';
import { ChevronRight, Lock } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import { useHaptic } from '@utils/haptic';
import { LIGHT_THEME_MODES, type ThemeMode } from '@constants/colors';
import { sidebarSubAnchor } from '@/features/onboarding';

interface LinkItem {
  label: string;
  link: string;
  icon: React.ReactElement;
  isMinimized: boolean;
  subLinks?: LinkItem[];
  badge?: string;
  /** Translation key for the label, resolved against the active language. */
  i18nKey?: string;
  /** Close mobile drawer after navigating */
  onNavigate?: () => void;
  /** Module disabled for this company: dim the row and block navigation. */
  locked?: boolean;
  /** Opens the contact-admin dialog when a locked row is clicked. */
  onLockedClick?: () => void;
  /**
   * Overrides the accordion's own open state. Used by the onboarding film,
   * which renders the real rail and scripts it; undefined keeps the row
   * self-governing, which is every other render.
   */
  forceOpened?: boolean;
  /**
   * Treat this sub-link (by `link`) as the active one instead of deriving it
   * from the route. `''` forces "none". Same caller as `forceOpened`.
   */
  forceActiveSubLink?: string;
}

/** Translate a sidebar badge; falls back to the original text when unknown. */
const BADGE_KEYS: Record<string, string> = {
  BOD: 'badge.bod',
  New: 'badge.new',
  Mulai: 'badge.mulai',
  'AI Ready': 'badge.aiReady',
};

/** Themes whose sidebar surface is light — active pills must use dark text. */
const LIGHT_SIDEBAR_MODES: ThemeMode[] = LIGHT_THEME_MODES;

/* ------------------------------------------------------------------ *
 *  Badge contrast
 *
 *  The badge chip ("BARU", "BOD") is 10px uppercase on a translucent pill,
 *  so it needs 4.5:1 — and no single palette token delivers that across the
 *  themes. Measured against the chip's own background, `accent` falls below
 *  4.5:1 in 8 of the 12 dark sidebars (as low as 2.95:1 on `blue`, which is
 *  what made BARU hard to read), and `primary` still fails in 3.
 *
 *  So the palette supplies the hue and this walks it toward white on a dark
 *  sidebar, or black on a light one, stopping the moment it clears the ratio.
 *  Themes whose token already passes come through untouched.
 * ------------------------------------------------------------------ */

type Rgb = [number, number, number];

const hexToRgb = (hex: string): Rgb => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [128, 128, 128];
  const n = Number.parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const rgbToCss = ([r, g, b]: Rgb) => `rgb(${r}, ${g}, ${b})`;

/** `src` laid over `dst` at `alpha`. */
const composite = (src: Rgb, alpha: number, dst: Rgb): Rgb =>
  [0, 1, 2].map((i) => Math.round(alpha * src[i] + (1 - alpha) * dst[i])) as Rgb;

const relativeLuminance = ([r, g, b]: Rgb): number => {
  const channel = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

const contrastRatio = (a: Rgb, b: Rgb): number => {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

/**
 * Nudge `color` toward `toward` in 5% steps until it clears `target` contrast
 * against `bg`. Returns the first step that passes, or full `toward` if even
 * that cannot (which only happens when `bg` itself is mid-grey).
 */
const ensureContrast = (color: string, bg: Rgb, toward: Rgb, target = 4.5): string => {
  const base = hexToRgb(color);
  if (contrastRatio(base, bg) >= target) return rgbToCss(base);
  for (let step = 0.05; step < 1; step += 0.05) {
    const mixed = composite(toward, step, base);
    if (contrastRatio(mixed, bg) >= target) return rgbToCss(mixed);
  }
  return rgbToCss(toward);
};

const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];
/** The light-sidebar chip tint, also used as its pill background. */
const LIGHT_CHIP_INK = '#0F6B5C';

/* ------------------------------------------------------------------ *
 *  Motion presets — futuristic micro-interactions
 * ------------------------------------------------------------------ */

/** Staggered entrance for each top-level row (fade + lift + breathe). */
const rowEntrance = (index: number) => ({
  initial: { opacity: 0, x: -14, filter: 'blur(3px)' },
  animate: { opacity: 1, x: 0, filter: 'blur(0px)' },
  transition: {
    delay: 0.06 * index + 0.12,
    duration: 0.38,
    ease: [0.22, 1, 0.36, 1] as const,
  },
});

/** Icon micro-pop on hover / active. */
const iconHover = {
  scale: 1.18,
  transition: { type: 'spring' as const, stiffness: 380, damping: 18 },
};

/** Animated submenu expand / collapse. */
const subMenuMotion = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { duration: 0.26, ease: [0.22, 1, 0.36, 1] as const },
};

/**
 * A sub-link matches its own path and its descendant routes — `/foo` covers
 * `/foo/123` but never `/foobar`. Bare prefix matching is not enough on its
 * own: siblings often nest (`/customer-approval` is the parent path of
 * `/customer-approval/email-groups`), so `activeSubLinkIndex` picks the
 * longest match and only that one lights up.
 */
function isSubLinkActive(pathName: string | undefined, link: string): boolean {
  const basePath = link.split('?')[0];
  const currentBasePath = pathName?.split('?')[0];
  if (!currentBasePath || !basePath || basePath === '#') return false;
  if (basePath === '/') return currentBasePath === '/';
  return currentBasePath === basePath || currentBasePath.startsWith(`${basePath}/`);
}

/** Index of the sub-link owning the current path; -1 when none match. */
function activeSubLinkIndex(pathName: string | undefined, subLinks?: LinkItem[]): number {
  let best = -1;
  let bestLength = -1;
  subLinks?.forEach((subItem, index) => {
    if (!isSubLinkActive(pathName, subItem.link)) return;
    const length = subItem.link.split('?')[0].length;
    if (length > bestLength) {
      bestLength = length;
      best = index;
    }
  });
  return best;
}

export default function LinksGroup(item: LinkItem) {
  const location = useLocation();
  const pathName = location.pathname;
  const [openedState, setOpened] = useState(false);
  const opened = item.forceOpened ?? openedState;
  const { mode, colors: themeColors } = useTheme();
  const { t } = useLanguage();
  const hapticNav = useHaptic('light');
  const hapticToggle = useHaptic('medium');

  const labelOf = (entry: LinkItem) => (entry.i18nKey ? t(entry.i18nKey) : entry.label);
  const badgeOf = (badge?: string) => (badge ? (BADGE_KEYS[badge] ? t(BADGE_KEYS[badge]) : badge) : undefined);

  const activeSubIndex =
    item.forceActiveSubLink !== undefined
      ? item.subLinks?.findIndex((subItem) => subItem.link === item.forceActiveSubLink) ?? -1
      : activeSubLinkIndex(pathName, item.subLinks);

  useEffect(() => {
    if (pathName?.startsWith(item.link) || activeSubLinkIndex(pathName, item.subLinks) >= 0) {
      setOpened(true);
    }
  }, [pathName, item.link, item.subLinks]);

  const hasActiveSubLink = activeSubIndex >= 0;
  const isActive =
    hasActiveSubLink ||
    pathName === item.link ||
    (pathName?.startsWith(`${item.link}/`) && item.link !== '/' && item.link !== '/doc-pabean');

  // dark / navy / etc. need translucent white pills — not #F2F2F2 (was washing out labels)
  const isDarkSidebar = !LIGHT_SIDEBAR_MODES.includes(mode);
  const accent = themeColors.accent ?? themeColors.primary ?? '#2A6B62';
  const activeBg = isDarkSidebar ? 'rgba(255, 255, 255, 0.14)' : '#E7F0ED';
  const activeBorder = isDarkSidebar ? 'rgba(255,255,255,0.22)' : 'rgba(42, 107, 98, 0.28)';
  const hoverBg = isDarkSidebar ? 'rgba(255, 255, 255, 0.07)' : 'rgba(42, 107, 98, 0.08)';
  const textColor = isDarkSidebar ? 'rgba(255, 255, 255, 0.85)' : themeColors.text;
  const activeTextColor = isDarkSidebar ? '#FFFFFF' : themeColors.text;
  const mutedText = isDarkSidebar ? 'rgba(255, 255, 255, 0.55)' : themeColors.textSecondary;
  const guideColor = isDarkSidebar ? 'rgba(255, 255, 255, 0.18)' : `linear-gradient(180deg, ${accent}66, transparent)`;

  // Chip background is a 10% wash over the sidebar surface; resolve it so the
  // label's contrast is measured against what it actually sits on.
  const sidebarSurface = hexToRgb(themeColors.sidebar ?? themeColors.background ?? '#1A221F');
  const chipBg = composite(isDarkSidebar ? WHITE : hexToRgb(LIGHT_CHIP_INK), 0.1, sidebarSurface);
  const badgeColor = ensureContrast(
    isDarkSidebar ? themeColors.primary ?? accent : LIGHT_CHIP_INK,
    chipBg,
    isDarkSidebar ? WHITE : BLACK
  );

  const rowBase = 'w-full flex items-center rounded-xl group relative';
  const expandedRow = `${rowBase} gap-2.5 px-3 py-2`;
  const minimizedRow = `${rowBase} justify-center p-2`;

  const handleNavigate = () => {
    hapticNav();
    item.onNavigate?.();
  };

  const BadgeChip = item.badge ? (
    <span
      className="ml-auto shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded-md uppercase tracking-wide"
      style={{
        color: badgeColor,
        backgroundColor: isDarkSidebar ? 'rgba(255,255,255,0.10)' : 'rgba(42, 107, 98, 0.10)',
        border: `1px solid ${isDarkSidebar ? 'rgba(255,255,255,0.16)' : 'rgba(42, 107, 98, 0.28)'}`,
      }}
    >
      {badgeOf(item.badge)}
    </span>
  ) : null;

  const IconSlot = (
    <span
      className="flex items-center justify-center text-[15px] shrink-0"
      style={{ color: isActive ? activeTextColor : mutedText, width: 18 }}
    >
      {item.icon}
    </span>
  );

  const SubLinksList = (
    <motion.div {...subMenuMotion} style={{ overflow: 'hidden' }} className="ml-4 pl-3">
      <div className="mt-0.5 space-y-0.5" style={{ borderLeft: `1px solid ${guideColor}` }}>
        {item.subLinks?.map((subItem, i) => {
          const isSubActive = i === activeSubIndex;
          return (
            <motion.div
              key={subItem.link}
              data-tour={sidebarSubAnchor(item.label, subItem.label)}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.05 * i, duration: 0.22, ease: 'easeOut' }}
            >
              <Link
                to={subItem.link}
                onClick={handleNavigate}
                className="flex items-center gap-2.5 pl-2.5 pr-2 py-1.5 rounded-lg text-[13px] transition-colors duration-150"
                style={{
                  backgroundColor: isSubActive ? activeBg : 'transparent',
                  color: isSubActive ? activeTextColor : textColor,
                  fontWeight: isSubActive ? 600 : 400,
                  border: isSubActive ? `1px solid ${activeBorder}` : '1px solid transparent',
                }}
              >
                <motion.span
                  className="text-[13px] shrink-0"
                  style={{ color: isSubActive ? activeTextColor : mutedText }}
                  whileHover={iconHover}
                >
                  {subItem.icon}
                </motion.span>
                <span className="truncate">{labelOf(subItem)}</span>
                {isSubActive && (
                  <motion.span
                    layoutId={`sidebar-indicator-${item.label}`}
                    className="ml-auto h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: accent, boxShadow: `0 0 8px ${accent}` }}
                  />
                )}
              </Link>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );

  // Locked module: one dimmed row for every variant — no flyout, no
  // accordion, no navigation; clicking explains how to activate it.
  if (item.locked) {
    const lockedButton = (
      <motion.button
        type="button"
        {...rowEntrance(0)}
        onClick={() => {
          hapticNav();
          item.onLockedClick?.();
        }}
        className={item.isMinimized ? minimizedRow : expandedRow}
        style={{ border: '1px solid transparent', cursor: 'pointer', opacity: 0.55 }}
        whileTap={{ scale: 0.97 }}
        aria-disabled
      >
        {IconSlot}
        {!item.isMinimized && (
          <>
            <span className="text-[13px] font-medium truncate flex-1 text-left">{labelOf(item)}</span>
            <span className="shrink-0 flex items-center" style={{ color: mutedText }}>
              <Lock size={12} strokeWidth={1.5} />
            </span>
          </>
        )}
      </motion.button>
    );

    if (item.isMinimized) {
      return (
        <Tooltip label={labelOf(item)} position="right" withArrow openDelay={300}>
          {lockedButton}
        </Tooltip>
      );
    }
    return lockedButton;
  }

  // Minimized + subLinks: hover flyout so deep links stay reachable
  if (item.subLinks && item.isMinimized) {
    return (
      <Menu
        trigger="hover"
        position="right-start"
        offset={8}
        transitionProps={{ transition: 'fade', duration: 150 }}
        styles={{
          dropdown: {
            backgroundColor: themeColors.card,
            border: `1px solid ${themeColors.border}`,
            borderRadius: 8,
            padding: 6,
            minWidth: 200,
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          },
        }}
      >
        <Menu.Target>
          <Tooltip label={labelOf(item)} position="right" withArrow openDelay={300}>
            <motion.button
              type="button"
              {...rowEntrance(0)}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.backgroundColor = hoverBg;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isActive ? activeBg : 'transparent';
              }}
              className={minimizedRow}
              style={{
                ...(isActive
                  ? {
                      backgroundColor: activeBg,
                      border: `1px solid ${activeBorder}`,
                      boxShadow: `0 4px 18px -6px ${accent}55`,
                    }
                  : { border: '1px solid transparent' }),
              }}
              whileTap={{ scale: 0.95 }}
              aria-label={labelOf(item)}
            >
              {IconSlot}
              {item.badge && (
                <span
                  className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: themeColors.error }}
                />
              )}
            </motion.button>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          <div
            className="px-2.5 py-1.5 mb-1 text-[11px] font-semibold uppercase tracking-wide"
            style={{ color: mutedText }}
          >
            {labelOf(item)}
          </div>
          {item.subLinks.map((subItem, i) => {
            const isSubActive = i === activeSubIndex;
            return (
              <Menu.Item
                key={subItem.link}
                component={Link}
                to={subItem.link}
                onClick={handleNavigate}
                leftSection={
                  <motion.span
                    whileTap={{ scale: 0.85 }}
                    style={{ fontSize: 14, color: isSubActive ? accent : undefined }}
                  >
                    {subItem.icon}
                  </motion.span>
                }
                style={{
                  backgroundColor: isSubActive ? activeBg : 'transparent',
                  color: isSubActive ? activeTextColor : textColor,
                  fontWeight: isSubActive ? 600 : 400,
                  borderRadius: 6,
                  fontSize: 13,
                }}
              >
                {labelOf(subItem)}
              </Menu.Item>
            );
          })}
        </Menu.Dropdown>
      </Menu>
    );
  }

  // Expanded (or mobile): inline accordion for items with subLinks
  if (item.subLinks) {
    return (
      <div style={{ position: 'relative' }}>
        {isActive && (
          <motion.span
            layoutId="sidebar-active-column"
            className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-full jv-active-glow"
            style={{ backgroundColor: accent, boxShadow: `0 0 12px ${accent}` }}
          />
        )}
        <motion.button
          type="button"
          onClick={() => {
            hapticToggle();
            setOpened(!opened);
          }}
          {...rowEntrance(0)}
          className={item.isMinimized ? minimizedRow : expandedRow}
          onMouseEnter={(e) => {
            if (!isActive) e.currentTarget.style.backgroundColor = hoverBg;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = isActive ? activeBg : 'transparent';
          }}
          style={{
            ...(isActive
              ? {
                  backgroundColor: activeBg,
                  border: `1px solid ${activeBorder}`,
                  boxShadow: `0 4px 18px -6px ${accent}55`,
                }
              : { border: '1px solid transparent' }),
            cursor: 'pointer',
          }}
          whileTap={{ scale: 0.985 }}
        >
          {IconSlot}
          {!item.isMinimized && (
            <>
              <span className="text-[13px] font-medium truncate flex-1 text-left">{labelOf(item)}</span>
              {BadgeChip}
              <motion.span
                animate={{ rotate: opened ? 90 : 0 }}
                transition={{ duration: 0.22, ease: 'easeOut' }}
                className="shrink-0 flex items-center"
                style={{ color: mutedText, display: 'flex' }}
              >
                <ChevronRight size={12} strokeWidth={1.5} />
              </motion.span>
            </>
          )}
        </motion.button>
        {!item.isMinimized && <AnimatePresence initial={false}>{opened && SubLinksList}</AnimatePresence>}
      </div>
    );
  }

  // Leaf link
  const leafButton = (
    <motion.button
      type="button"
      {...rowEntrance(0)}
      className={item.isMinimized ? minimizedRow : expandedRow}
      onMouseEnter={(e) => {
        if (!isActive) e.currentTarget.style.backgroundColor = hoverBg;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = isActive ? activeBg : 'transparent';
      }}
      style={{
        ...(isActive
          ? {
              backgroundColor: activeBg,
              border: `1px solid ${activeBorder}`,
              boxShadow: `0 4px 18px -6px ${accent}55`,
            }
          : { border: '1px solid transparent' }),
        cursor: 'pointer',
      }}
      whileTap={{ scale: isActive ? 1 : 0.97 }}
    >
      {IconSlot}
      {!item.isMinimized && (
        <>
          <span className="text-[13px] font-medium truncate flex-1 text-left">{labelOf(item)}</span>
          {BadgeChip}
        </>
      )}
    </motion.button>
  );

  if (item.isMinimized) {
    return (
      <Tooltip label={labelOf(item)} position="right" withArrow openDelay={300}>
        <Link to={item.link} className="block" onClick={handleNavigate}>
          {leafButton}
        </Link>
      </Tooltip>
    );
  }

  return (
    <Link to={item.link} className="block" onClick={handleNavigate}>
      {leafButton}
    </Link>
  );
}
